import { browserOnly } from '../../../shared/util/browserOnly';
import type { IInput, PointerState } from './IInput';

/**
 * DOMInput — IInput impl wrapping window/document listeners.
 *
 * Phase H10 Arc ζ (2026-05-08): singleton, started once per browser
 * session via `start()`. Idempotent — safe to call multiple times.
 *
 * Per-key listener Sets allow many subscribers per key without trampling.
 * Unsubscribe functions remove the specific callback (no leaks).
 *
 * The legacy gameplay InputSystem still owns its own key tracking +
 * window listeners (action-mapped keysJustPressed buffer for jump, etc.).
 * That's intentional — DOMInput is the simpler surface for ad-hoc
 * subscribers. The two coexist; both attach DOM listeners independently.
 */
class DOMInputImpl implements IInput {
  private started = false;
  private keysDown = new Set<string>();
  private keyDownListeners = new Map<string, Set<() => void>>();
  private keyUpListeners = new Map<string, Set<() => void>>();
  private pointerDownListeners = new Set<(e: PointerEvent) => void>();
  private pointerUpListeners = new Set<(e: PointerEvent) => void>();
  private pointerMoveListeners = new Set<(e: PointerEvent) => void>();
  private wheelListeners = new Set<(e: WheelEvent) => void>();
  private contextMenuListeners = new Set<(e: MouseEvent) => void>();

  private pointerState: { x: number; y: number; buttons: number } = { x: 0, y: 0, buttons: 0 };

  get pointer(): PointerState {
    return this.pointerState;
  }

  start(): void {
    if (this.started) return;
    browserOnly(() => {
      this.started = true;
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('keyup', this.handleKeyUp);
      window.addEventListener('blur', this.handleBlur);
      window.addEventListener('pointerdown', this.handlePointerDown);
      window.addEventListener('pointerup', this.handlePointerUp);
      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('wheel', this.handleWheel, { passive: false });
      window.addEventListener('contextmenu', this.handleContextMenu);
    });
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    browserOnly(() => {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('keyup', this.handleKeyUp);
      window.removeEventListener('blur', this.handleBlur);
      window.removeEventListener('pointerdown', this.handlePointerDown);
      window.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('wheel', this.handleWheel);
      window.removeEventListener('contextmenu', this.handleContextMenu);
    });
    this.keysDown.clear();
  }

  isKeyDown(key: string): boolean {
    return this.keysDown.has(key);
  }

  onKeyDown(key: string, fn: () => void): () => void {
    let set = this.keyDownListeners.get(key);
    if (!set) {
      set = new Set();
      this.keyDownListeners.set(key, set);
    }
    set.add(fn);
    return () => { set?.delete(fn); };
  }

  onKeyUp(key: string, fn: () => void): () => void {
    let set = this.keyUpListeners.get(key);
    if (!set) {
      set = new Set();
      this.keyUpListeners.set(key, set);
    }
    set.add(fn);
    return () => { set?.delete(fn); };
  }

  onPointerDown(fn: (e: PointerEvent) => void): () => void {
    this.pointerDownListeners.add(fn);
    return () => { this.pointerDownListeners.delete(fn); };
  }

  onPointerUp(fn: (e: PointerEvent) => void): () => void {
    this.pointerUpListeners.add(fn);
    return () => { this.pointerUpListeners.delete(fn); };
  }

  onPointerMove(fn: (e: PointerEvent) => void): () => void {
    this.pointerMoveListeners.add(fn);
    return () => { this.pointerMoveListeners.delete(fn); };
  }

  onWheel(fn: (e: WheelEvent) => void): () => void {
    this.wheelListeners.add(fn);
    return () => { this.wheelListeners.delete(fn); };
  }

  onContextMenu(fn: (e: MouseEvent) => void): () => void {
    this.contextMenuListeners.add(fn);
    return () => { this.contextMenuListeners.delete(fn); };
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    // Backtick hard-ignore (2026-06-10): it crashed the client via the editor
    // toggle; never dispatch it to subscribers, even if one registered for it.
    if (e.code === 'Backquote') return;
    // First-press only: skip OS key-repeat.
    if (this.keysDown.has(e.code)) return;
    this.keysDown.add(e.code);
    const set = this.keyDownListeners.get(e.code);
    if (set) for (const fn of set) fn();
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    this.keysDown.delete(e.code);
    const set = this.keyUpListeners.get(e.code);
    if (set) for (const fn of set) fn();
  };

  private handleBlur = (): void => {
    // Window lost focus — release all keys to prevent stuck-key bugs.
    this.keysDown.clear();
  };

  private handlePointerDown = (e: PointerEvent): void => {
    this.pointerState = { x: e.clientX, y: e.clientY, buttons: e.buttons };
    for (const fn of this.pointerDownListeners) fn(e);
  };

  private handlePointerUp = (e: PointerEvent): void => {
    this.pointerState = { x: e.clientX, y: e.clientY, buttons: e.buttons };
    for (const fn of this.pointerUpListeners) fn(e);
  };

  private handlePointerMove = (e: PointerEvent): void => {
    this.pointerState = { x: e.clientX, y: e.clientY, buttons: e.buttons };
    for (const fn of this.pointerMoveListeners) fn(e);
  };

  private handleWheel = (e: WheelEvent): void => {
    for (const fn of this.wheelListeners) fn(e);
  };

  private handleContextMenu = (e: MouseEvent): void => {
    for (const fn of this.contextMenuListeners) fn(e);
  };
}

export const DOMInput = new DOMInputImpl();

/** Boot-layer call: start the global window listeners. Idempotent. */
export function start(): void {
  DOMInput.start();
}

/** Test-only: stop listeners and clear key state. */
export function stop(): void {
  DOMInput.stop();
}
